import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { Icon } from "@iconify/react";
import CloseOutlined from "@mui/icons-material/CloseOutlined";

import {
  Autocomplete,
  Checkbox,
  Dialog,
  FormControlLabel,
  IconButton,
  TextField,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import "./DistributeMethodDialog.css";
import "../Components/Components.css";
import EditTicket from "../Components/EditTicket";

import {
  assignTicket,
  getAgentFilter,
  getListTicket,
} from "../Store/Actions/TicketAction";

const DistributeMethodDialog = ({
  isBulkEditing,
  isOpen,
  selectedTicket,
  setIsBulkEditing,
  setIsDistributeMethodDialogShown,
  statusOptions,
}) => {
  const [currentStep, setCurrentStep] = useState(1);

  const initialParams = {
    // Range initialized as 1 year before current date
    datestart: moment().subtract(1, "years").format("YYYY-MM-DD"),

    dateend: moment().format("YYYY-MM-DD"),
    isassign: null,
    skip: 0,

    // Initialized with new and in progress
    status: [2, 3],

    take: 999,
  };

  const [searchParams, setSearchParams] = useState(initialParams);
  const [isNewLoading, setIsNewLoading] = useState(true);
  const [isUnassignedLoading, setIsUnassignedLoading] = useState(true);
  const [isAgentListLoading, setIsAgentListLoading] = useState(true);
  const [newTickets, setNewTickets] = useState(0);
  const [unassignedTickets, setUnassignedTickets] = useState(0);
  const [ticketToDistributeAmount, setTicketToDistributeAmount] = useState(0);
  // const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState("");

  const initialDisplayValues = {
    status: [],
  };

  // Display values for autocomplete components
  const [displayValues, setDisplayValues] = useState(initialDisplayValues);
  const [isLimited, setIsLimited] = useState(false);
  const [limit, setLimit] = useState(null);
  const [listAgent, setListAgent] = useState([]);
  const [agentsToAssign, setAgentsToAssign] = useState([]);
  const [ticketsToAssign, setTicketsToAssign] = useState([]);
  const { myInfo } = useSelector((state) => state.auth);

  useEffect(() => {
    // If there is selected ticket, choose the selection option and set the ticket amount
    if (selectedTicket.length) {
      setSelectedMethod("selection");
      setTicketToDistributeAmount(selectedTicket.length);
      setTicketsToAssign(selectedTicket);
    }
  }, [selectedTicket.length]);

  useEffect(() => {
    // Initialize display values. useEffect needed because statusOptions is initialized as undefined
    statusOptions &&
      setDisplayValues({
        status: statusOptions?.filter((option) => option.StatusID !== 1),
      });
  }, [statusOptions]);

  // Get new tickets
  useEffect(() => {
    setIsNewLoading(true);

    getListTicket({
      ...searchParams,
      status: 2,
    })
      .then((response) => {
        if (response.status) {
          process.env.NODE_ENV === "development" &&
            console.log("List new ticket", response.data.ticket[0]);

          setNewTickets(response.data.ticket[0]);
          setIsNewLoading(false);
        } else {
          console.log("error", response.error);
          setIsNewLoading(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsNewLoading(false);
      });
  }, []);

  // Get unassigned tickets
  useEffect(() => {
    setIsUnassignedLoading(true);

    getListTicket({
      ...searchParams,

      // If no status is selected, default to new and in progress
      status: searchParams.status ? searchParams.status : [2, 3],

      isassign: 0,
    })
      .then((response) => {
        if (response.status) {
          process.env.NODE_ENV === "development" &&
            console.log("List unassigned ticket", response.data.ticket[0]);

          setUnassignedTickets(response.data.ticket[0]);

          setTicketsToAssign(
            response.data.ticket[0].map(
              (unassignedTicket) => unassignedTicket.CaseID
            )
          );

          setIsUnassignedLoading(false);
        } else {
          console.log("error", response.error);
          setIsUnassignedLoading(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsUnassignedLoading(false);
      });
  }, [searchParams.status]);

  useEffect(() => {
    if (currentStep === 2) {
      setIsAgentListLoading(true);

      // Get agent data for ticket distribution
      getAgentFilter(myInfo.user.AgentID)
        .then((response) => {
          if (response.status) {
            console.log("agent data", response.data);
            setListAgent(response.data);
            setIsAgentListLoading(false);
          } else {
            console.log("error", response.error);
            setIsAgentListLoading(false);
          }
        })
        .catch((err) => {
          console.log("err", err);
          setIsAgentListLoading(false);
        });
    }
  }, [currentStep]);

  const columnDistribution = [
    {
      field: "AgentCode",
      flex: 1,
      headerName: "Agent Code",
    },

    {
      field: "AgentName",
      flex: 1,
      headerName: "Agent Name",
    },

    {
      field: "ActiveTicket",
      flex: 0.5,
      headerName: "Pending",
    },

    {
      field: "Percent",
      flex: 0.5,
      headerName: "Percent",

      renderCell: (params) => {
        const calculatePercentage = (assignedTicketsAmount) => {
          let percentage =
            (assignedTicketsAmount / ticketToDistributeAmount) * 100;

          return percentage.toFixed(2);
        };

        const index = params.api.getRowIndex(params.row.AgentID) + 1;
        const remainder = ticketToDistributeAmount % agentsToAssign.length;

        if (agentsToAssign.includes(params.id)) {
          // Distribute the ticket evenly
          if (index <= remainder) {
            // Return with remainder
            return (
              calculatePercentage(
                Math.floor(ticketToDistributeAmount / agentsToAssign.length) + 1
              ) + "%"
            );
          } else {
            // Return without remainder
            return (
              calculatePercentage(
                Math.floor(ticketToDistributeAmount / agentsToAssign.length)
              ) + "%"
            );
          }
        } else {
          return "0%";
        }
      },
    },
  ];

  const columnConfirmation = [
    {
      field: "AgentName",
      flex: 1,
      headerName: "Assigned Agent",
    },

    {
      field: "Capacity",
      flex: 0.5,
      headerName: "Capacity",
    },

    {
      field: "ActiveTicket",
      flex: 0.5,
      headerName: "Ongoing",
    },

    {
      field: "New",
      flex: 0.5,
      headerName: "New",

      renderCell: (params) => {
        const index = params.api.getRowIndex(params.row.AgentID) + 1;
        const remainder = ticketToDistributeAmount % agentsToAssign.length;

        if (index <= remainder) {
          // Return with remainder
          return (
            Math.floor(ticketToDistributeAmount / agentsToAssign.length) + 1
          );
        } else {
          // Return without remainder
          return Math.floor(ticketToDistributeAmount / agentsToAssign.length);
        }
      },
    },

    {
      field: "Total",
      flex: 0.5,
      headerName: "Total",

      renderCell: (params) => {
        const index = params.api.getRowIndex(params.row.AgentID) + 1;
        const remainder = ticketToDistributeAmount % agentsToAssign.length;

        if (index <= remainder) {
          // Return with remainder
          return (
            Math.floor(ticketToDistributeAmount / agentsToAssign.length) +
            1 +
            (params.row.ActiveTicket || 0)
          );
        } else {
          // Return without remainder
          return (
            Math.floor(ticketToDistributeAmount / agentsToAssign.length) +
            (params.row.ActiveTicket || 0)
          );
        }
      },
    },
  ];

  const handleClose = () => {
    setSelectedMethod("");
    setIsDistributeMethodDialogShown(false);
    setCurrentStep(1);
    setTicketToDistributeAmount(0);
    setIsLimited(false);
    setLimit(null);
    setAgentsToAssign([]);
    setTicketsToAssign([]);
    setSearchParams(initialParams);
    setDisplayValues(initialDisplayValues);
    setIsBulkEditing(false);
  };

  const handleDisabledConfirmButton = () => {
    if (currentStep === 1) {
      return selectedMethod === "" ? true : false;
    } else if (currentStep === 2) {
      return !agentsToAssign.length ? true : false;
    }
  };

  const handleConfirm = () => {
    console.log("ticketsToAssign", ticketsToAssign);

    // Sort the tickets ascending
    let sortedTickets = ticketsToAssign.sort((a, b) => a - b);

    // Limit the tickets, if there is limit value
    const LimitedTickets = limit
      ? sortedTickets.slice(0, limit)
      : sortedTickets;

    let payload = {
      assignby: myInfo.user.AgentID,

      // Assign the tickets evenly to agents
      ticket: LimitedTickets.map((ticket, index) => {
        return {
          caseid: ticket,
          userid: agentsToAssign[index % agentsToAssign.length],
        };
      }),
    };

    console.log("payload", payload);

    assignTicket(payload)
      .then((response) => {
        if (response.status) {
          handleClose();

          toast.success("Tickets have been assigned.", {
            autoClose: 3000,
          });
        } else {
          console.log("error", response.error);
          handleClose();

          toast.error("Error assigning ticket.", {
            autoClose: 3000,
          });
        }
      })
      .catch((err) => {
        console.log("err", err);
        handleClose();

        toast.error("Error assigning ticket.", {
          autoClose: 3000,
        });
      });
  };

  return (
    <Dialog
      open={isOpen}
      className="dialog distribute-method-dialog"
      fullWidth
      maxWidth="md"
      scroll="paper"
      onClose={handleClose}
    >
      <div className="dialog-container">
        <div className="dialog-content">
          <h2 className="title">
            {isBulkEditing
              ? "Bulk Edit Ticket"
              : currentStep === 3
                ? "Distribution Confirmation"
                : "Distribute Method"}
          </h2>

          {currentStep === 1 && (
            <div className="method-list">
              {selectedTicket.length ? (
                <div
                  className={`method selection ${
                    selectedMethod === "selection" ? "selected" : ""
                  }`}
                  onClick={() => {
                    setSelectedMethod("selection");
                    setTicketToDistributeAmount(selectedTicket.length);
                    setTicketsToAssign(selectedTicket);
                  }}
                >
                  <Icon height="34" icon="ci:select-multiple" />

                  <div className="name-selected">
                    <p className="method-name">Set Selected</p>

                    <p className="selected-amount">
                      {selectedTicket.length} Ticket Selected
                    </p>
                  </div>
                </div>
              ) : (
                ""
              )}

              <div
                className={`method new ${
                  selectedMethod === "new" ? "selected" : ""
                }`}
                onClick={() => {
                  setSelectedMethod("new");
                  setTicketToDistributeAmount(newTickets.length);

                  setTicketsToAssign(
                    newTickets.map((newTicket) => newTicket.CaseID),
                  );
                }}
              >
                <Icon height="34" icon="material-symbols:fiber-new-outline" />

                <div className="name-selected">
                  <p className="method-name">Select All New</p>

                  <p className="selected-amount">
                    {isNewLoading
                      ? "Loading..."
                      : `${newTickets.length} Ticket Selected`}
                  </p>
                </div>
              </div>

              <div
                className={`method unassigned ${
                  selectedMethod === "unassigned" ? "selected" : ""
                }`}
                onClick={() => {
                  setSelectedMethod("unassigned");
                  setTicketToDistributeAmount(unassignedTickets.length);

                  setTicketsToAssign(
                    unassignedTickets.map(
                      (unassignedTicket) => unassignedTicket.CaseID,
                    ),
                  );
                }}
              >
                <Icon height="34" icon="mingcute:filter-2-fill" />

                <div className="name-selected">
                  <p className="method-name">
                    Select Unassigned Ticket by Status
                  </p>

                  <p className="selected-amount">
                    {isUnassignedLoading
                      ? "Loading..."
                      : `${unassignedTickets.length} Ticket Selected`}
                  </p>
                </div>

                <Autocomplete
                  className="search-item status-selector"
                  filterSelectedOptions
                  multiple
                  // Hide Closed option
                  options={statusOptions?.filter(
                    (option) => option.StatusID !== 1,
                  )}
                  value={displayValues.status}
                  getOptionLabel={(status) => status.Description}
                  onChange={(event, value) => {
                    setSearchParams({
                      ...searchParams,
                      status: value.length
                        ? value.map((val) => val.StatusID)
                        : "",
                    });

                    setDisplayValues({
                      ...displayValues,
                      status: value,
                    });

                    console.log("displayValues", displayValues);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Status" />
                  )}
                />
              </div>
            </div>
          )}

          {currentStep === 2 &&
            (isBulkEditing ? (
              <EditTicket
                isBulkEditing
                handleCloseBulkEdit={handleClose}
                handlePrev={() => setCurrentStep((prevState) => prevState - 1)}
                listBulkEditTicketID={ticketsToAssign}
              />
            ) : (
              <DataGrid
                checkboxSelection
                className="table"
                columns={columnDistribution}
                disableColumnFilter
                disableColumnMenu
                disableSelectionOnClick
                hideFooter
                keepNonExistentRowsSelected
                loading={isAgentListLoading}
                rows={listAgent}
                selectionModel={agentsToAssign}
                getRowId={(row) => row.AgentID}
                onSelectionModelChange={(newAgentsToAssign) => {
                  // Limit selected agents according to number of tickets
                  if (newAgentsToAssign.length <= ticketToDistributeAmount) {
                    setAgentsToAssign(newAgentsToAssign);
                  }
                }}
              />
            ))}

          {currentStep === 3 && (
            <DataGrid
              className="table"
              columns={columnConfirmation}
              disableColumnFilter
              disableColumnMenu
              disableSelectionOnClick
              hideFooter
              loading={isAgentListLoading}
              rows={listAgent.filter((agent) =>
                agentsToAssign.includes(agent.AgentID),
              )}
              getRowId={(row) => row.AgentID}
            />
          )}

          <IconButton className="close-button" onClick={handleClose}>
            <CloseOutlined />
          </IconButton>
        </div>

        {currentStep === 1 && (
          <section className="limit">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isLimited}
                  onChange={(e) => setIsLimited(e.target.checked)}
                />
              }
              label="Limit Result to"
            />

            <TextField
              disabled={!isLimited}
              type="number"
              value={limit}
              onChange={(e) => {
                setLimit(e.target.value);
                setTicketToDistributeAmount(Number(e.target.value));
              }}
            />
          </section>
        )}

        {!(isBulkEditing && currentStep === 2) && (
          <section className="buttons">
            <button
              className="cancel"
              type="button"
              onClick={() => {
                currentStep === 1
                  ? handleClose()
                  : setCurrentStep((prevState) => prevState - 1);
              }}
            >
              {currentStep === 1 ? "Cancel" : "Prev"}
            </button>

            <button
              className="confirm"
              disabled={handleDisabledConfirmButton()}
              type="submit"
              onClick={() => {
                currentStep === 3
                  ? handleConfirm()
                  : setCurrentStep((prevState) => prevState + 1);
              }}
            >
              {currentStep === 3 ? "Confirm" : "Next"}
            </button>
          </section>
        )}
      </div>
    </Dialog>
  );
};

export default DistributeMethodDialog;
